import moment from 'moment';
import { createQuery, createQueries } from '@tanstack/svelte-query';
import { DATE_FORMAT } from '../lib/constants';
import {
  QUERY_KEY_CURATED_STATS_BY_CREATOR,
  QUERY_KEY_CURATED_STATS_BY_SHORTLINK,
} from '../constants';

const getDailyCuratedStats = async ({
  startDate,
  endDate,
  shortlinkId,
  socialPlatform,
  pageFilterPath,
}) => {
  if (!startDate || !endDate) return [];

  const currResponse = await fetch(
    `/api/daily-curated-stats?${new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      ...(shortlinkId && { shortlink_id: shortlinkId }),
      ...(pageFilterPath && { page_filter_path: pageFilterPath }),
      ...(socialPlatform && { social_platform: socialPlatform }),
    })}`,
  );
  const currData = await currResponse.json();
  const dateDiff = moment(endDate).diff(moment(startDate), 'days');
  const prevStartDate = moment(startDate)
    .subtract(dateDiff + 1, 'days')
    .format(DATE_FORMAT);
  const prevEndDate = moment(endDate)
    .subtract(dateDiff + 1, 'days')
    .format(DATE_FORMAT);
  const prevResponse = await fetch(
    `/api/daily-curated-stats?${new URLSearchParams({
      start_date: prevStartDate,
      end_date: prevEndDate,
      ...(shortlinkId && { shortlink_id: shortlinkId }),
      ...(pageFilterPath && { page_filter_path: pageFilterPath }),
      ...(socialPlatform && { social_platform: socialPlatform }),
    })}`,
  );
  const prevData = await prevResponse.json();
  const change = {
    total_shortlink_clicks:
      currData.object.total_shortlink_clicks -
      prevData.object.total_shortlink_clicks,
    total_page_views:
      currData.object.total_page_views - prevData.object.total_page_views,
  };
  return {
    currentData: currData.object,
    previousData: prevData.object,
    change,
  };
};

export const queryDailyCuratedStatsByCreator = (
  { startDate, endDate, socialPlatform },
  queryOptions,
) =>
  createQuery({
    queryKey: [
      QUERY_KEY_CURATED_STATS_BY_CREATOR,
      {
        startDate,
        endDate,
        socialPlatform,
      },
    ],
    queryFn: () =>
      getDailyCuratedStats({
        startDate,
        endDate,
        socialPlatform,
      }),
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions,
  });

export const queryDailyCuratedStatsByShortlink = (
  { pages, startDate, endDate, socialPlatform },
  queryOptions,
) =>
  createQueries({
    queries: pages.map((page) => {
      return {
        queryKey: [
          QUERY_KEY_CURATED_STATS_BY_SHORTLINK,
          {
            startDate,
            endDate,
            shortlinkId: page.curatedByPage?.shortlink,
            socialPlatform,
          },
        ],
        queryFn: () =>
          getDailyCuratedStats({
            startDate,
            endDate,
            shortlinkId: page.curatedByPage?.shortlink,
            socialPlatform,
          }),
        enabled: !!page.curatedByPage?.shortlink,
        refetchOnWindowFocus: false,
        ...queryOptions,
      };
    }),
  });

export const queryDailyCuratedStatsByPage = (
  { pages, startDate, endDate, socialPlatform },
  queryOptions,
) =>
  createQueries({
    queries: pages.map((page) => {
      return {
        queryKey: [
          QUERY_KEY_CURATED_STATS_BY_SHORTLINK,
          {
            startDate,
            endDate,
            pageFilterPath: page?.path,
            socialPlatform,
          },
        ],
        queryFn: () =>
          getDailyCuratedStats({
            startDate,
            endDate,
            pageFilterPath: page?.path,
            socialPlatform,
          }),
        enabled: !!page?.path,
        refetchOnWindowFocus: false,
        ...queryOptions,
      };
    }),
  });
